import {
  getAccessToken,
  notifySessionExpired,
  refreshAccessToken,
} from './authTokenStore'
import { ApiError, type ProblemDetails } from './problem'

/**
 * Basis-URL. Tom streng betyder relative kald, som Vite-proxyen og
 * nginx/Caddy videresender til API'et. Det holder dev og produktion ens.
 */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

/**
 * Endpoints der ikke skal udløse et fornyelsesforsøg ved 401.
 * Uden dette ville et mislykket login sende os i en fornyelsesløkke.
 */
const NO_REFRESH_PATHS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/register']

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Serialiseres som JSON. Brug `formData` til filuploads. */
  body?: unknown
  formData?: FormData
  /**
   * Sendes uændret. Til rå bytes — fx ét bid af en flerdelt upload, hvor
   * hverken JSON eller en multipart-form giver mening.
   */
  rawBody?: BodyInit
  /** Query-parametre. `undefined` og `null` udelades. */
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** Springer Authorization-headeren over. Bruges til offentlige endpoints. */
  anonymous?: boolean
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  }

  const queryString = params.toString()
  return queryString ? `${url}?${queryString}` : url
}

async function parseProblem(response: Response): Promise<ProblemDetails> {
  try {
    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('json')) {
      return (await response.json()) as ProblemDetails
    }
  } catch {
    // Kroppen var ikke gyldig JSON. Vi falder tilbage til statusteksten
    // nedenfor frem for at kaste en ny fejl oven i den oprindelige.
  }

  return {
    title: response.statusText || 'Fejl',
    status: response.status,
    detail: 'Serveren returnerede et uventet svar.',
  }
}

/**
 * Ét sted for alle HTTP-kald.
 *
 * - Access tokenet sættes automatisk på som Bearer.
 * - `credentials: 'include'` sender refresh token-cookien med.
 * - Ved 401 fornys tokenet én gang, og kaldet gentages. Lykkes det ikke,
 *   får appen besked om at sessionen er udløbet.
 * - Alle fejl bliver til `ApiError`, så kaldere kun har én type at håndtere.
 */
export async function apiFetch<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  return await request(path, options, readBody<TResponse>)
}

/**
 * Samme kald, men svaret læses som en fil.
 *
 * Findes til indhold, API'et selv genererer i det øjeblik, der bliver spurgt —
 * i dag CSV-eksporten af en rapport. De filer, brugere har uploadet, hentes
 * derimod gennem præsignerede URL'er direkte fra objektlageret og går slet ikke
 * denne vej.
 *
 * Fornyelsen af tokenet ved 401 og oversættelsen af fejl til `ApiError` er den
 * samme som for JSON — det er netop derfor det ligger her og ikke som et rå
 * `fetch` ude i en enkelt feature.
 */
export async function apiBlob(
  path: string,
  options: Omit<RequestOptions, 'body' | 'formData' | 'rawBody'> = {},
): Promise<Blob> {
  return await request(path, options, (response) => response.blob())
}

/**
 * Rå fetch med samme 401-fornyelse som resten af klienten.
 *
 * Findes til de hentninger, der læser svaret som en fil og ejer deres egne
 * fejlbeskeder — tegnings- og dokumentviserne. De stod før med et rå `fetch`
 * uden fornyelse, og et access token, der udløb, mens en viser stod åben,
 * blev til en fejl frem for en stille fornyelse.
 *
 * Ved 401 fornys tokenet én gang — sammenlagt med alle andre igangværende
 * fornyelser, se `refreshAccessToken` — og kaldet gentages. Svaret returneres
 * rått: kalderen afgør selv, hvad et 409 eller en manglende fil skal hedde.
 */
export async function fetchWithAuthRetry(
  path: string,
  init: Omit<RequestInit, 'headers'> & { headers?: HeadersInit } = {},
): Promise<Response> {
  // Tokenet læses ved hvert forsøg, så gentagelsen bruger det fornyede.
  const doFetch = () => {
    const requestHeaders = new Headers(init.headers)
    const token = getAccessToken()
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`)
    }

    return fetch(buildUrl(path), { ...init, headers: requestHeaders, credentials: 'include' })
  }

  const response = await doFetch()

  if (response.status !== 401) {
    return response
  }

  const refreshedToken = await refreshAccessToken()

  if (!refreshedToken) {
    notifySessionExpired()
    return response
  }

  const retried = await doFetch()

  if (retried.status === 401) {
    notifySessionExpired()
  }

  return retried
}

/**
 * Selve kaldet, uafhængigt af hvordan kroppen skal læses.
 *
 * Ved 401 fornys tokenet én gang, og kaldet gentages. Lykkes det ikke, får
 * appen besked om at sessionen er udløbet.
 */
async function request<TResult>(
  path: string,
  options: RequestOptions,
  read: (response: Response) => Promise<TResult>,
): Promise<TResult> {
  const response = await send(path, options)

  if (response.ok) {
    return await read(response)
  }

  const canRetry =
    response.status === 401 &&
    !options.anonymous &&
    !NO_REFRESH_PATHS.some((candidate) => path.startsWith(candidate))

  if (!canRetry) {
    throw new ApiError(response.status, await parseProblem(response))
  }

  const refreshedToken = await refreshAccessToken()

  if (!refreshedToken) {
    notifySessionExpired()
    throw new ApiError(response.status, await parseProblem(response))
  }

  const retried = await send(path, options)

  if (!retried.ok) {
    if (retried.status === 401) {
      notifySessionExpired()
    }
    throw new ApiError(retried.status, await parseProblem(retried))
  }

  return await read(retried)
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { body, formData, rawBody, query, headers, anonymous, ...init } = options

  const requestHeaders = new Headers(headers)

  // JSON er svaret på næsten alt, men ikke på alt: en CSV-eksport beder om
  // text/csv. Kalderens egen header vinder derfor frem for at blive skrevet
  // over her.
  if (!requestHeaders.has('Accept')) {
    requestHeaders.set('Accept', 'application/json')
  }

  if (!anonymous) {
    const token = getAccessToken()
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`)
    }
  }

  let requestBody: BodyInit | undefined
  if (formData) {
    // Content-Type sættes ikke: browseren skal selv tilføje multipart-boundary.
    requestBody = formData
  } else if (rawBody !== undefined) {
    // Kalderen sætter selv Content-Type; vi rører hverken indhold eller header.
    requestBody = rawBody
  } else if (body !== undefined) {
    requestHeaders.set('Content-Type', 'application/json')
    requestBody = JSON.stringify(body)
  }

  try {
    return await fetch(buildUrl(path, query), {
      ...init,
      headers: requestHeaders,
      body: requestBody,
      credentials: 'include',
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause
    }
    throw ApiError.network(cause)
  }
}

async function readBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, 'body' | 'method'>) =>
    apiFetch<T>(path, { ...options, method: 'POST', formData }),
}
